import { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress,
    Alert,
    Stack,
    Divider,
    Grid
} from '@mui/material';
import {
    Visibility as VisibilityIcon,
    Refresh as RefreshIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import { getClientPrincipal } from '../services/auth';
import { fetchMyPPDs, deletePPD, PPDFormClient } from '../services/ppd';

const MyPPDPage = () => {
    const [ppdForms, setPpdForms] = useState<PPDFormClient[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedPPD, setSelectedPPD] = useState<PPDFormClient | null>(null);
    const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deletingPPD, setDeletingPPD] = useState<PPDFormClient | null>(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        loadPPDForms();
    }, []);

    const loadPPDForms = async () => {
        try {
            setLoading(true);
            setError(null);
            const principal = await getClientPrincipal();
            if (!principal?.userDetails) {
                throw new Error('User not authenticated');
            }

            const forms = await fetchMyPPDs(principal.userDetails);
            setPpdForms(forms);
        } catch (err) {
            console.error('Error loading PPD forms:', err);
            setError('Failed to load PPD forms');
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetails = (ppd: PPDFormClient) => {
        setSelectedPPD(ppd);
        setDetailsDialogOpen(true);
    };

    const handleDeleteClick = (ppd: PPDFormClient) => {
        setDeletingPPD(ppd);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!deletingPPD?.id) return;
        try {
            setDeleting(true);
            await deletePPD(deletingPPD.id);
            setDeleteDialogOpen(false);
            setDeletingPPD(null);
            await loadPPDForms();
        } catch (err) {
            console.error('Error deleting PPD form:', err);
            alert('Failed to delete PPD form');
        } finally {
            setDeleting(false);
        }
    };

    const getStatusColor = (status: string): "default" | "warning" | "success" | "error" => {
        switch (status) {
            case 'pending':
                return 'warning';
            case 'approved':
                return 'success';
            case 'rejected':
                return 'error';
            default:
                return 'default';
        }
    };

    const getCompetencyNames = (competencyIds: number[]): string => {
        const competencyMap: { [key: number]: string } = {
            1: 'Values and behaviours',
            2: 'Consultancy skills and processes',
            3: 'Technical knowledge and skills',
            4: 'Leadership and management',
            5: 'Business Development and Delivery'
        };

        return competencyIds.map(id => competencyMap[id] || 'Unknown').join(', ');
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert severity="error">{error}</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Paper elevation={3} sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4" sx={{ color: '#009688' }}>
                        My PPD Forms
                    </Typography>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={loadPPDForms}
                    >
                        Refresh
                    </Button>
                </Box>

                {ppdForms.length === 0 ? (
                    <Alert severity="info">
                        You have not submitted any PPD forms yet.
                    </Alert>
                ) : (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Activity</TableCell>
                                    <TableCell>Job Title</TableCell>
                                    <TableCell>Submitted</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {ppdForms.map((ppd) => (
                                    <TableRow key={ppd.id}>
                                        <TableCell>{ppd.activity || '-'}</TableCell>
                                        <TableCell>{ppd.job_title || '-'}</TableCell>
                                        <TableCell>
                                            {ppd.submission_date 
                                                ? new Date(ppd.submission_date).toLocaleDateString()
                                                : ppd.created_at 
                                                ? new Date(ppd.created_at).toLocaleDateString()
                                                : 'N/A'}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={ppd.status.toUpperCase()}
                                                color={getStatusColor(ppd.status)}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                onClick={() => handleViewDetails(ppd)}
                                                size="small"
                                                title="View details"
                                            >
                                                <VisibilityIcon />
                                            </IconButton>
                                            <IconButton
                                                onClick={() => handleDeleteClick(ppd)}
                                                size="small"
                                                title="Delete form"
                                                color="error"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            {/* Details Dialog */}
            <Dialog 
                open={detailsDialogOpen} 
                onClose={() => setDetailsDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ bgcolor: '#009688', color: 'white' }}>
                    PPD Form Details
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    {selectedPPD && (
                        <Stack spacing={2}>
                            {/* Status */}
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Status
                                </Typography>
                                <Chip
                                    label={selectedPPD.status.toUpperCase()}
                                    color={getStatusColor(selectedPPD.status)}
                                    sx={{ mt: 0.5 }}
                                />
                            </Box>

                            <Divider />

                            {/* Header Information */}
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Job Title
                                    </Typography>
                                    <Typography variant="body1">
                                        {selectedPPD.job_title || '-'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Manager Email
                                    </Typography>
                                    <Typography variant="body1">
                                        {selectedPPD.manager_email}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Activity
                                    </Typography>
                                    <Typography variant="body1">
                                        {selectedPPD.activity || '-'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Provider
                                    </Typography>
                                    <Typography variant="body1">
                                        {selectedPPD.provider_if_external || '-'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Duration
                                    </Typography>
                                    <Typography variant="body1">
                                        {selectedPPD.duration || '-'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Cost (GBP)
                                    </Typography>
                                    <Typography variant="body1">
                                        £{selectedPPD.cost_gbp || 0}
                                    </Typography>
                                </Grid>
                            </Grid>

                            <Divider />

                            {/* Questions */}
                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                    1. Development Need
                                </Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {selectedPPD.development_need || '-'}
                                </Typography>
                            </Box>

                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                    2. Competencies Addressed
                                </Typography>
                                <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                                    {selectedPPD.selected_competencies && selectedPPD.selected_competencies.length > 0 ? (
                                        selectedPPD.selected_competencies.map((compId) => (
                                            <Chip
                                                key={compId}
                                                label={getCompetencyNames([compId])}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                            />
                                        ))
                                    ) : (
                                        <Typography variant="body2">None selected</Typography>
                                    )}
                                </Stack>
                            </Box>

                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                    3. Options Considered
                                </Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {selectedPPD.options_considered || '-'}
                                </Typography>
                            </Box>

                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                    4. Resources Required
                                </Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {selectedPPD.resources_required || '-'}
                                </Typography>
                            </Box>

                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                    5. Timescales
                                </Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {selectedPPD.timescales || '-'}
                                </Typography>
                            </Box>

                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                    6. Effectiveness Assessment
                                </Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {selectedPPD.effectiveness_assessment || '-'}
                                </Typography>
                            </Box>

                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                    7. Expected Changes
                                </Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {selectedPPD.expected_changes || '-'}
                                </Typography>
                            </Box>

                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                    8. Knowledge Sharing
                                </Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {selectedPPD.knowledge_sharing || '-'}
                                </Typography>
                            </Box>

                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                    9. Proposed Option
                                </Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {selectedPPD.proposed_option || '-'}
                                </Typography>
                            </Box>

                            {/* Approval/Rejection Info */}
                            {selectedPPD.status === 'approved' && (
                                <Box sx={{ bgcolor: '#e8f5e9', p: 2, borderRadius: 1 }}>
                                    <Typography variant="subtitle2" fontWeight="bold" color="success.dark">
                                        ✅ Approved
                                    </Typography>
                                    <Typography variant="body2">
                                        Approved by: {selectedPPD.approved_by || 'N/A'}
                                    </Typography>
                                    <Typography variant="body2">
                                        Date: {selectedPPD.approval_date 
                                            ? new Date(selectedPPD.approval_date).toLocaleDateString()
                                            : 'N/A'}
                                    </Typography>
                                </Box>
                            )}

                            {selectedPPD.status === 'rejected' && (
                                <Box sx={{ bgcolor: '#fff3e0', p: 2, borderRadius: 1 }}>
                                    <Typography variant="subtitle2" fontWeight="bold" color="error.dark">
                                        ❌ Requires Changes
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        <strong>Reason:</strong>
                                    </Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                                        {selectedPPD.rejection_reason || 'No reason provided'}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        Reviewed by: {selectedPPD.approved_by || 'N/A'}
                                    </Typography>
                                </Box>
                            )}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailsDialogOpen(false)}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => !deleting && setDeleteDialogOpen(false)}
            >
                <DialogTitle>Delete PPD Form</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete this PPD form
                        {deletingPPD?.activity ? ` for "${deletingPPD.activity}"` : ''}?
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        variant="contained"
                        color="error"
                        disabled={deleting}
                    >
                        {deleting ? 'Deleting...' : 'Yes, Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default MyPPDPage;
